// src/components/Footer.js
import React from "react";
import { Mail, Phone, MessageCircle, MapPin, Facebook, Instagram } from "lucide-react";
import tvqlogo from "../assets/tvqlogo.png";

const Footer = ({ visible = true, scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }) }) => {
  return (
    <footer
      id="footer"
      className={`bg-gray-900 text-white py-12 transition-all duration-1000 ${
        visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Brand + Social */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white rounded-full p-3 shadow-xl border-4 border-yellow-400">
                <img src={tvqlogo} alt="Virtual Quran Academy" className="h-12 w-12 rounded-full object-cover" />
              </div>
              <h3 className="text-2xl font-bold text-yellow-500">The Virtual Quran Academy</h3>
            </div>

            <p className="text-gray-300">
              Making Quranic education accessible to everyone through personalized online learning.
            </p>

            <div className="mt-6">
              <p className="text-sm text-gray-400 mb-2">Follow us</p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/thevirtualquranacademey"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/thevirtualquranacademy"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Home", id: "home" },
                { label: "About", id: "about" },
                { label: "Teachers", id: "teachers" },
                { label: "Classes", id: "classes" },
                { label: "Events", id: "events" },
                { label: "Blogs", id: "blogs" },
                { label: "Contact", id: "contact" }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-gray-300 hover:text-yellow-500 text-left text-sm transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact Info (icons only + label) */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
            <div className="space-y-4 text-sm text-gray-300">
              <div className="flex items-center gap-3">
                <div className="bg-gray-800 p-2 rounded-md">
                  <Mail className="w-5 h-5 text-yellow-500" />
                </div>
                <a href="mailto:info@thevirtualquranacademy.com" className="hover:text-yellow-500">info@thevirtualquranacademy.com</a>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-gray-800 p-2 rounded-md">
                  <Phone className="w-5 h-5 text-yellow-500" />
                </div>
                <a href="tel:+923152457297" className="hover:text-yellow-500">+92 315 2457297</a>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-gray-800 p-2 rounded-md">
                  <MessageCircle className="w-5 h-5 text-yellow-500" />
                </div>
                <a href="https://wa.me/971525619185" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-500">+971 525619185</a>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-gray-800 p-2 rounded-md">
                  <MapPin className="w-5 h-5 text-yellow-500" />
                </div>
                <span>Karachi, Pakistan</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 text-center text-gray-400 text-sm border-t border-gray-800">
          <p className="mb-2">&copy; 2024 Virtual Quran Academy. All rights reserved.</p>
          <p>
            Developed by{" "}
            <a href="https://www.omaanu.com/" target="_blank" rel="noopener noreferrer" className="text-yellow-500 hover:text-yellow-400 underline">
              Omaanu.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
